<template>
  <div class="scrollable-div display-scrollbar" ref="scrollableDiv">
    <Confirmation
      v-if="showStartConfirmation"
      header="Start New Search"
      message="Starting a new search will clear the current results. Are you sure you want to continue?"
      confirmText="Start Search"
      cancelText="Cancel"
      :serious="false"
      @confirm="confirmStartSearch"
      @cancel="showStartConfirmation = false" />
    <Confirmation
      v-if="showNotDuplicateConfirmation"
      header="Mark as Not Duplicate"
      message="Are you sure you want to mark these companies as not duplicates?"
      confirmText="Confirm"
      cancelText="Cancel"
      :serious="false"
      @confirm="confirmNotDuplicate"
      @cancel="showNotDuplicateConfirmation = null" />
    <MergeDuplicates v-if="showMergeDuplicate" :duplicate="showMergeDuplicate" @closePopup="showMergeDuplicate = null" @duplicateMerged="handleDuplicateMerged" />
    <table class="full-width-table">
      <tbody>
        <tr class="table-header">
          <th style="width: 15%">Original {{ searchingType === 'companies' ? 'Company' : 'Individual' }}</th>
          <th style="width: 15%">Potential Duplicate</th>
          <th style="flex: 1">Duplicate Certainty</th>
          <th style="width: 12rem"></th>
          <th style="width: 8rem"></th>
        </tr>
        <tr v-for="duplicate in sortedDuplicates">
          <td class="company-info">
            <div class="name" v-if="duplicate.original.name" @click="$router.push({ name: 'companies', params: { companyid: duplicate.original.id } })">{{ duplicate.original.name }}</div>
            <div class="name" v-if="duplicate.original.firstName || duplicate.original.lastName" @click="$router.push({ name: 'contacts', params: { contactid: duplicate.original.id } })">
              {{ duplicate.original.firstName }} {{ duplicate.original.lastName }}
            </div>
            <div class="name" v-if="duplicate.original.companyName" @click="$router.push({ name: 'companies', params: { companyid: duplicate.original.id } })">{{ duplicate.original.companyName }}</div>
            <div v-if="duplicate.original.address?.line1">{{ duplicate.original.address.line1 }}</div>
            <div v-if="duplicate.original.address?.line2">{{ duplicate.original.address.line2 }}</div>
            <div v-if="duplicate.original.address?.city">
              {{ duplicate.original.address.city ? duplicate.original.address.city + ', ' : '' }}
              {{ duplicate.original.address.state }}
              {{ duplicate.original.address.postalCode }}
            </div>
            <div v-if="duplicate.original.mainPhone">{{ duplicate.original.mainPhone }}</div>
            <div v-if="duplicate.original.tollFreePhone">{{ duplicate.original.tollFreePhone }}</div>
            <div v-if="duplicate.original.mobilePhone">{{ duplicate.original.mobilePhone }}</div>
            <div v-if="duplicate.original.email">{{ duplicate.original.email }}</div>
            <div v-if="duplicate.original.webAddress">
              <a :href="duplicate.original.webAddress" target="_blank">{{ duplicate.original.webAddress }}</a>
            </div>
          </td>
          <td class="company-info">
            <div class="name" v-if="duplicate.duplicate.name" @click="$router.push({ name: 'companies', params: { companyid: duplicate.duplicate.id } })">{{ duplicate.duplicate.name }}</div>
            <div class="name" v-if="duplicate.duplicate.firstName || duplicate.duplicate.lastName" @click="$router.push({ name: 'contacts', params: { contactid: duplicate.duplicate.id } })">
              {{ duplicate.duplicate.firstName }} {{ duplicate.duplicate.lastName }}
            </div>
            <div class="name" v-if="duplicate.duplicate.companyName" @click="$router.push({ name: 'companies', params: { companyid: duplicate.duplicate.id } })">{{ duplicate.duplicate.companyName }}</div>
            <div v-if="duplicate.duplicate.address?.line1">{{ duplicate.duplicate.address.line1 }}</div>
            <div v-if="duplicate.duplicate.address?.line2">{{ duplicate.duplicate.address.line2 }}</div>
            <div v-if="duplicate.duplicate.address?.city">
              {{ duplicate.duplicate.address.city ? duplicate.duplicate.address.city + ', ' : '' }}
              {{ duplicate.duplicate.address.state }}
              {{ duplicate.duplicate.address.postalCode }}
            </div>
            <div v-if="duplicate.duplicate.mainPhone">{{ duplicate.duplicate.mainPhone }}</div>
            <div v-if="duplicate.duplicate.tollFreePhone">{{ duplicate.duplicate.tollFreePhone }}</div>
            <div v-if="duplicate.duplicate.mobilePhone">{{ duplicate.duplicate.mobilePhone }}</div>
            <div v-if="duplicate.duplicate.email">{{ duplicate.duplicate.email }}</div>
            <div v-if="duplicate.duplicate.webAddress">
              <a :href="'https://' + duplicate.duplicate.webAddress" target="_blank">{{ duplicate.duplicate.webAddress }}</a>
            </div>
          </td>
          <td :class="['certainty', getDuplicateCertainty(duplicate.similarity).toLowerCase()]">{{ getDuplicateCertainty(duplicate.similarity) }}</td>
          <td><button class="text-btn" @click="showNotDuplicateConfirmation = duplicate">NOT DUPLICATE</button></td>
          <td><button class="square-btn short" @click="showMergeDuplicate = duplicate" style="position: inherit">MERGE</button></td>
        </tr>
        <tr v-if="isSearching && duplicates.length === 0" class="loading-row">
          <td colspan="5">
            <img src="../../assets/spinner.svg" class="inline-block" />
          </td>
        </tr>
        <tr v-if="!isSearching && duplicates.length === 0" class="no-results">
          <td colspan="5">No duplicates found</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import useAIFindDuplicates from '@/composables/useAIFindDuplicates';
import { watch, ref } from 'vue';
import Confirmation from '@/components/Confirmation.vue';
import MergeDuplicates from '@/components/MergeDuplicates.vue';

export default {
  name: 'FindDuplicates',
  components: {
    Confirmation,
    MergeDuplicates,
  },
  props: {
    duplicateType: {
      type: String,
      required: true,
    },
  },
  emits: ['searchStatusChanged'],
  setup(props, { emit }) {
    const { duplicates, progress, isSearching, startDuplicateCheck, stopDuplicateCheck, clearResults, setNotDuplicate, searchingType } = useAIFindDuplicates();

    // Watch for changes in search status and emit to parent
    watch(isSearching, (newValue) => {
      emit('searchStatusChanged', newValue);
    });

    return {
      duplicates,
      progress,
      isSearching,
      startDuplicateCheck,
      stopDuplicateCheck,
      clearResults,
      setNotDuplicate,
      searchingType,
    };
  },
  data() {
    return {
      showStartConfirmation: false,
      showNotDuplicateConfirmation: null,
      showMergeDuplicate: null,
      similarityThreshold: {
        companies: {
          high: 0.925,
          medium: 0.875,
        },
        contacts: {
          high: 0.93,
          medium: 0.88,
        },
      },
    };
  },
  computed: {
    sortedDuplicates() {
      return [...this.duplicates].sort((a, b) => {
        // First sort by certainty
        const certaintyA = this.getDuplicateCertainty(a.similarity);
        const certaintyB = this.getDuplicateCertainty(b.similarity);

        const certaintyOrder = { High: 0, Medium: 1, Low: 2 };
        const certaintyCompare = certaintyOrder[certaintyA] - certaintyOrder[certaintyB];

        if (certaintyCompare !== 0) {
          return certaintyCompare;
        }

        // If certainty is the same, sort alphabetically by original company name
        if (this.searchingType === 'companies') {
          return a.original.name.localeCompare(b.original.name);
        } else {
          const aName = a.original.firstName + ' ' + a.original.lastName;
          const bName = b.original.firstName + ' ' + b.original.lastName;
          return aName.localeCompare(bName);
        }
      });
    },
  },
  methods: {
    startSearch() {
      if (this.duplicates.length > 0) {
        this.showStartConfirmation = true;
      } else {
        this.startDuplicateCheck(this.duplicateType);
      }
    },
    confirmStartSearch() {
      this.showStartConfirmation = false;
      this.startDuplicateCheck(this.duplicateType);
    },
    stopSearch() {
      this.stopDuplicateCheck();
    },
    getDuplicateCertainty(similarity) {
      if (similarity >= this.similarityThreshold[this.duplicateType].high) {
        return 'High';
      } else if (similarity >= this.similarityThreshold[this.duplicateType].medium) {
        return 'Medium';
      } else {
        return 'Low';
      }
    },
    confirmNotDuplicate() {
      if (this.showNotDuplicateConfirmation) {
        this.setNotDuplicate(this.showNotDuplicateConfirmation.original.id, this.showNotDuplicateConfirmation.duplicate.id, this.duplicateType);
        this.duplicates = this.duplicates.filter((d) => d.original.id !== this.showNotDuplicateConfirmation.original.id && d.duplicate.id !== this.showNotDuplicateConfirmation.duplicate.id);
        this.showNotDuplicateConfirmation = null;
      }
    },
    handleDuplicateMerged() {
      this.duplicates = this.duplicates.filter(
        (d) =>
          d.original.id !== this.showMergeDuplicate.original.id &&
          d.duplicate.id !== this.showMergeDuplicate.duplicate.id &&
          d.original.id !== this.showMergeDuplicate.duplicate.id &&
          d.duplicate.id !== this.showMergeDuplicate.original.id
      );
      this.showMergeDuplicate = null;
    },
  },
  mounted() {
    // Emit initial search status to parent
    this.$emit('searchStatusChanged', this.isSearching);
  },
};
</script>

<style lang="scss" scoped>
.scrollable-div {
  height: 100%;
  overflow-y: auto;
}

.company-info {
  padding: 0.75rem;
  line-height: 1.4;

  div {
    font-size: $fontSize11;
    &:first-child {
      font-size: $fontSize13;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.loading-row img {
  width: 2rem;
  height: 2rem;
}

.merge-direction {
  text-align: center;

  img {
    width: 2rem;
    cursor: pointer;
    transition: transform 0.3s ease-in-out;
    &.rotate-180 {
      transform: rotate(180deg);
    }
  }
}

.certainty {
  font-weight: 600;

  &.high {
    color: $alertColor; // Green for high certainty
  }

  &.medium {
    color: $alertBlueColor; // Blue for medium certainty
  }

  &.low {
    color: $secondaryActionColor; // Gray for low certainty
  }
}
</style>
